/**
 * ListFilterDropdown — Push 79 (2026-07-23) shared filter-dropdown primitive
 * ============================================================================
 * Extracted from SettingsUsage's SpendLimitsByUserSection FilterPopover
 * (Push 78 anchor). Push 79 rolls the list-view pattern (PATTERNS.md § D
 * "List view shape") across People / AdminLibraryList / requests inbox / etc;
 * each one carries the same portal-anchored filter dropdown with:
 *
 *   - Trigger button with IconFunnel + label + optional active dot badge
 *   - Portal-rendered popover anchored under the trigger's bottom-right
 *   - ESC key + click-outside dismiss
 *   - Groups of dimensions (single-select radio OR multi-select checkbox)
 *   - Optional "Reset filters" footer link when any dimension differs from
 *     defaults
 *
 * The DNA matches Push 63 (Claude Desktop chat-rail filter submenu):
 * anchored popover, active checkmark, right-aligned to trigger, ESC + click-
 * outside dismiss. The primitive keeps the trigger simple + delegates content
 * to a `dimensions` prop so surface-specific filter shapes compose cleanly.
 *
 * Filter state persistence is the caller's responsibility (localStorage per-
 * surface key per Push 78 discipline). This primitive is pure UI — it emits
 * onChange events and renders the popover; the parent owns the state.
 *
 * Usage:
 *
 *   const [role, setRole] = useState<'all'|'owner'|'admin'|'user'>('all');
 *   const [tags, setTags] = useState<Set<string>>(new Set());
 *
 *   <ListFilterDropdown
 *     ariaLabel="Filter people"
 *     activeCount={ (role !== 'all' ? 1 : 0) + tags.size }
 *     onReset={() => { setRole('all'); setTags(new Set()); }}
 *     dimensions={[
 *       {
 *         key: 'role',
 *         kind: 'single',
 *         label: 'Role',
 *         value: role,
 *         onChange: (v) => setRole(v as 'all'|'owner'|'admin'|'user'),
 *         options: [
 *           { value: 'all', label: 'All roles' },
 *           { value: 'owner', label: 'Owner' },
 *           { value: 'admin', label: 'Admin' },
 *           { value: 'user', label: 'User' },
 *         ],
 *       },
 *       {
 *         key: 'tags',
 *         kind: 'multi',
 *         label: 'Tags',
 *         values: tags,
 *         onChange: setTags,
 *         options: accountTags.map((t) => ({ value: t, label: t })),
 *       },
 *     ]}
 *   />
 */

import { Button } from '@olytic/foundation';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconCheck, IconFunnel } from './Icon.tsx';

// ---------------------------------------------------------------------------
// Dimension shape — each filter axis (Role / Status / Tags / etc) is either a
// single-select radio row OR a multi-select checkbox row.
// ---------------------------------------------------------------------------

export interface ListFilterOption {
  value: string;
  label: string;
  /** Optional inline count shown dim to the right of the label. */
  count?: number | undefined;
}

export type ListFilterDimension =
  | {
      key: string;
      kind: 'single';
      label: string;
      value: string;
      options: ListFilterOption[];
      onChange: (next: string) => void;
    }
  | {
      key: string;
      kind: 'multi';
      label: string;
      values: Set<string>;
      options: ListFilterOption[];
      onChange: (next: Set<string>) => void;
    };

// ---------------------------------------------------------------------------
// Trigger + popover
// ---------------------------------------------------------------------------

interface ListFilterDropdownProps {
  dimensions: ListFilterDimension[];
  /** Number of active filters — drives the dot-badge visibility. */
  activeCount: number;
  /** Fires when the user clicks the "Reset" footer link. Only rendered when
   *  activeCount > 0 AND an onReset handler is provided. */
  onReset?: (() => void) | undefined;
  /** Optional label rendered next to the funnel icon. Default: "Filter". */
  triggerLabel?: string | undefined;
  /** aria-label for the trigger button. Defaults to "Filters ({activeCount} active)". */
  ariaLabel?: string | undefined;
  /** Portal menu id — pass a stable string when multiple dropdowns can co-render
   *  on the same page, so the dismiss-click detection can find the right menu. */
  menuId?: string | undefined;
  /** Optional footer node rendered below the dimensions (e.g., "Clear tag filter"
   *  or a link to the workspace-tag manager). */
  footer?: ReactNode | undefined;
}

export function ListFilterDropdown({
  dimensions,
  activeCount,
  onReset,
  triggerLabel = 'Filter',
  ariaLabel,
  menuId = 'list-filter-menu',
  footer,
}: ListFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const isActive = activeCount > 0;
  const computedAria = ariaLabel ?? (isActive ? `Filters (${activeCount} active)` : 'Filters');

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={computedAria}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          position: 'relative',
        }}
      >
        <IconFunnel size={14} />
        <span style={{ fontSize: 12 }}>{triggerLabel}</span>
        {isActive && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--olytic-teal)',
              marginLeft: 2,
            }}
          />
        )}
      </Button>
      {open && (
        <FilterPopover
          triggerRef={triggerRef}
          menuId={menuId}
          dimensions={dimensions}
          activeCount={activeCount}
          {...(onReset !== undefined ? { onReset } : {})}
          {...(footer !== undefined ? { footer } : {})}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

interface FilterPopoverProps {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  dimensions: ListFilterDimension[];
  activeCount: number;
  onReset?: () => void;
  footer?: ReactNode;
  menuId: string;
  onClose: () => void;
}

function FilterPopover({
  triggerRef,
  dimensions,
  activeCount,
  onReset,
  footer,
  menuId,
  onClose,
}: FilterPopoverProps) {
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);

  // Measure the trigger + anchor the popover under its bottom-right on open,
  // re-anchor on scroll + resize.
  useEffect(() => {
    function compute() {
      const el = triggerRef.current;
      if (el === null) return;
      const r = el.getBoundingClientRect();
      const width = 240;
      let left = r.right - width;
      if (left < 8) left = 8;
      setRect({ top: r.bottom + 4, left });
    }
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [triggerRef]);

  // Dismiss on click-outside + Escape (Push 63 DNA).
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      const menu = document.getElementById(menuId);
      if (menu?.contains(t)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuId, onClose, triggerRef]);

  if (rect === null) return null;

  return createPortal(
    <div
      id={menuId}
      role="menu"
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: 240,
        maxHeight: 'min(60vh, 480px)',
        overflowY: 'auto',
        background: 'var(--bg-elevated, var(--olytic-white, #ffffff))',
        border: '1px solid var(--border)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.12))',
        padding: 4,
        zIndex: 50,
      }}
    >
      {dimensions.map((dim, dimIdx) => (
        <DimensionGroup key={dim.key} dim={dim} isFirst={dimIdx === 0} />
      ))}
      {footer !== undefined && (
        <div
          style={{
            marginTop: 4,
            padding: '6px 10px',
            borderTop: '1px solid var(--border)',
          }}
        >
          {footer}
        </div>
      )}
      {activeCount > 0 && onReset !== undefined && (
        <div
          style={{
            marginTop: 4,
            padding: '6px 10px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="button"
            variant="link"
            onClick={() => {
              onReset();
              onClose();
            }}
            style={{
              fontSize: 12,
              color: 'var(--olytic-teal)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Reset filters
          </Button>
        </div>
      )}
    </div>,
    document.body,
  );
}

function DimensionGroup({ dim, isFirst }: { dim: ListFilterDimension; isFirst: boolean }) {
  return (
    <div
      role="group"
      aria-label={dim.label}
      style={{
        padding: '4px 0',
        borderTop: isFirst ? undefined : '1px solid var(--border)',
        marginTop: isFirst ? 0 : 2,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--fg-muted)',
          fontWeight: 600,
          padding: '4px 10px 2px',
        }}
      >
        {dim.label}
      </div>
      {dim.options.map((opt) => (
        <OptionRow
          key={`${dim.key}:${opt.value}`}
          option={opt}
          active={dim.kind === 'single' ? dim.value === opt.value : dim.values.has(opt.value)}
          role={dim.kind === 'single' ? 'menuitemradio' : 'menuitemcheckbox'}
          onSelect={() => {
            if (dim.kind === 'single') {
              dim.onChange(opt.value);
            } else {
              const next = new Set(dim.values);
              if (next.has(opt.value)) next.delete(opt.value);
              else next.add(opt.value);
              dim.onChange(next);
            }
          }}
        />
      ))}
    </div>
  );
}

function OptionRow({
  option,
  active,
  role,
  onSelect,
}: {
  option: ListFilterOption;
  active: boolean;
  role: 'menuitemradio' | 'menuitemcheckbox';
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={active}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '7px 10px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: 13,
        color: 'var(--olytic-obsidian)',
        borderRadius: 6,
        textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--olytic-obsidian-08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <span>{option.label}</span>
        {option.count !== undefined && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--fg-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {option.count}
          </span>
        )}
      </span>
      {active && (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            color: 'var(--olytic-teal)',
            marginLeft: 8,
          }}
        >
          <IconCheck size={14} />
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helper — count active filter dimensions.
// A dimension is "active" if:
//   - single-select: value !== default (caller passes what "default" means)
//   - multi-select: values.size > 0
// This helper doesn't know the surface's defaults, so callers compute it
// inline. Exported for the tag-only multi-select shorthand:
// ---------------------------------------------------------------------------

export function useMemoActiveCount(computeCount: () => number, deps: unknown[]): number {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(computeCount, deps);
}
